import React, { useState, useMemo } from 'react';
import { Baby } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { HeightInput, WeightInput } from '../../components/calculators/MeasureInputs';
import { NumberField } from '../../components/calculators/FormFields';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { calculateBMI, pregnancyWeightGainRanges, recommendedGainAtWeek } from '../../lib/calculatorUtils';

const PregnancyWeightGainCalculatorPage = () => {
  const [unit, setUnit] = useState('metric');
  const [heightCm, setHeightCm] = useState('');
  const [prePregKg, setPrePregKg] = useState('');
  const [currentKg, setCurrentKg] = useState('');
  const [week, setWeek] = useState('');

  const result = useMemo(() => {
    const h = parseFloat(heightCm);
    const pre = parseFloat(prePregKg);
    const cur = parseFloat(currentKg);
    const w = parseInt(week, 10);
    if (!h || !pre || !w || w < 1 || w > 42) return null;
    const bmi = calculateBMI(pre, h);
    const ranges = pregnancyWeightGainRanges(bmi);
    const atWeek = recommendedGainAtWeek(w, ranges);
    const gainedKg = cur && cur > 0 ? +(cur - pre).toFixed(1) : null;
    return { bmi, ranges, atWeek, gainedKg };
  }, [heightCm, prePregKg, currentKg, week]);

  const status = (() => {
    if (!result?.gainedKg && result?.gainedKg !== 0) return null;
    if (result.gainedKg < result.atWeek.min) return { label: 'Below recommended range', color: 'text-amber-400' };
    if (result.gainedKg > result.atWeek.max) return { label: 'Above recommended range', color: 'text-orange-400' };
    return { label: 'Within recommended range', color: 'text-emerald-400' };
  })();

  const inputs = (
    <div className="space-y-5" data-testid="pw-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Your Pregnancy Details</h2>
      <p className="text-slate-400 text-sm mb-5">Based on the Institute of Medicine (IOM 2009) recommendations.</p>
      <HeightInput unit={unit} onUnitChange={setUnit} heightCm={heightCm} onHeightCmChange={setHeightCm} />
      <WeightInput unit={unit} onUnitChange={setUnit} weightKg={prePregKg} onWeightKgChange={setPrePregKg} label="Pre-Pregnancy Weight" />
      <WeightInput unit={unit} onUnitChange={setUnit} weightKg={currentKg} onWeightKgChange={setCurrentKg} label="Current Weight (optional)" />
      <NumberField id="pw-week" label="Current Gestational Week" value={week} onChange={setWeek} placeholder="e.g. 24" suffix="weeks" min={1} max={42} testId="pw-week-input" required />
      <p className="text-slate-500 text-xs">Always continue routine care with your obstetrician. This calculator is informational only.</p>
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label={`Recommended gain by week ${week}`} value={`${result.atWeek.min} – ${result.atWeek.max}`} unit="kg" accent="from-pink-500 to-rose-500" testId="pw-result-week-range" />
      <div className="mt-8 space-y-1">
        <StatRow label="Pre-pregnancy BMI" value={result.bmi} testId="pw-bmi" />
        <StatRow label="BMI category" value={result.ranges.category} testId="pw-bmi-category" />
        <StatRow label="Total recommended gain" value={`${result.ranges.total.min} – ${result.ranges.total.max}`} unit="kg" testId="pw-total-gain" />
        <StatRow label="Weekly (2nd & 3rd tri)" value={`${result.ranges.weekly2nd3rd.min} – ${result.ranges.weekly2nd3rd.max}`} unit="kg/wk" testId="pw-weekly-gain" />
        {result.gainedKg !== null && (
          <StatRow label="Gained so far" value={result.gainedKg} unit="kg" color={status?.color} testId="pw-gained" />
        )}
        {status && (
          <StatRow label="Status" value={status.label} color={status.color} testId="pw-status" />
        )}
      </div>
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        Recommendations vary for twin pregnancies, high-risk pregnancies and underlying conditions. Always follow your obstetrician's guidance.
      </p>
    </div>
  ) : (
    <EmptyResult message="Enter height, pre-pregnancy weight and current week to see your recommended gain range." />
  );

  return (
    <CalculatorLayout
      slug="pregnancy-weight-gain" title="Pregnancy Weight Gain Calculator"
      subtitle="Track safe pregnancy weight gain by trimester, based on your pre-pregnancy BMI and IOM 2009 guidelines."
      metaDescription="Pregnancy Weight Gain Calculator using IOM 2009 recommended weight gain by trimester and gestational week."
      icon={Baby}
      accentGradient="from-pink-500 to-rose-500" accentColor="text-pink-400"
      badgeBg="bg-pink-500/10" badgeBorder="border-pink-500/30"
      inputs={inputs} result={resultNode}
      formulaText={<>
        <p>Recommended total gain (IOM 2009): Underweight 12.5–18 kg · Normal 11.5–16 kg · Overweight 7–11.5 kg · Obese 5–9 kg.</p>
        <p>First-trimester gain ≈ 2 kg, with linear weekly gain through 2nd and 3rd trimesters.</p>
      </>}
    />
  );
};

export default PregnancyWeightGainCalculatorPage;
